import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faPlus, faSearch, faTrash, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { useAccount } from '../contexts/AccountContext';
import { useAccountSettings } from '../hooks/useAccountSettings';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { getUserData } from '../utils/userUtils';
import DataTable, { createActionColumn } from './DataTable';
import StatusBadge from './StatusBadge';
import './Pages.css';

/**
 * MediaPlansPage component for managing media plans
 * Displays all media plans for the current account
 * Aligned with PacingPage layout and functionality
 */
const MediaPlansPage = () => {
  const navigate = useNavigate();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();
  const { settings: accountSettings, isLoading: settingsLoading } = useAccountSettings(accountId, user);

  const [mediaPlans, setMediaPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});

  // Edit plan name inline state
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [editingPlanName, setEditingPlanName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Check if media planner is enabled for this account
  const mediaPlannerEnabled = accountSettings?.['media-planner-enabled'];

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  // Search-as-you-type against GET /api/v2/media-plans — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final term.
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Fetch media plans
  const fetchMediaPlans = useCallback(async () => {
    if (!accountId || !user) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        accountId,
        userId: user?.userId || user?.id,
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm })
      });

      const response = await api.get(getApiUrl(`/api/v2/media-plans?${params}`));

      if (response.status === 200) {
        setMediaPlans(response.data.plans || []);
        setPagination(response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10
        });
      }
    } catch (err) {
      console.error('Error fetching media plans:', err);
      setError(err.response?.data?.error || 'Failed to load media plans');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, user, currentPage, itemsPerPage, sortBy, sortOrder, debouncedSearchTerm]);

  useEffect(() => {
    if (accountId && user && mediaPlannerEnabled) {
      fetchMediaPlans();
    }
  }, [fetchMediaPlans, accountId, user, mediaPlannerEnabled]);

  // Handle search with debounce
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle sort
  const requestSort = (key) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Handle delete
  const handleDelete = async (plan, onClose) => {
    if (!window.confirm(`Are you sure you want to delete "${plan.name}"?`)) {
      if (onClose) onClose();
      return;
    }

    try {
      await api.delete(getApiUrl(`/api/v2/media-plans/${plan.id}`), {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });
      setMediaPlans(prev => prev.filter(p => p.id !== plan.id));
      setPagination(prev => ({
        ...prev,
        totalItems: Math.max(0, prev.totalItems - 1)
      }));
    } catch (err) {
      console.error('Error deleting media plan:', err);
      setError(err.response?.data?.error || 'Failed to delete media plan');
    }
    if (onClose) onClose();
  };

  // Handle edit plan name
  const handleEditPlan = (plan) => {
    setEditingPlanId(plan.id);
    setEditingPlanName(plan.name);
    setError(null);
  };

  const handleUpdatePlan = async () => {
    if (!editingPlanName.trim()) return;

    try {
      setIsUpdating(true);
      setError(null);

      const response = await api.patch(getApiUrl(`/api/v2/media-plans/${editingPlanId}`), {
        name: editingPlanName.trim()
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        // Update the plan in local state
        setMediaPlans(prev => prev.map(p =>
          p.id === editingPlanId
            ? { ...p, name: editingPlanName.trim() }
            : p
        ));

        // Exit edit mode
        setEditingPlanId(null);
        setEditingPlanName('');
      }
    } catch (err) {
      console.error('Error updating media plan:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update media plan';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlanId(null);
    setEditingPlanName('');
    setError(null);
  };

  // Handle pin/unpin
  const handlePin = async (plan, onClose) => {
    try {
      const response = await api.patch(getApiUrl(`/api/v2/media-plans/${plan.id}`), {
        is_pinned: !plan.is_pinned
      }, {
        params: {
          accountId,
          userId: user?.userId || user?.id
        }
      });

      if (response.status === 200) {
        // Update the plan's pin status
        const updatedPlans = mediaPlans.map(p =>
          p.id === plan.id ? { ...p, is_pinned: !p.is_pinned } : p
        );

        // Sort plans with pinned items first, then by current sort criteria
        const sortedPlans = updatedPlans.sort((a, b) => {
          // Pinned plans first
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;

          // Within same pin status, sort by current sort criteria
          let comparison = 0;

          if (sortBy === 'name') {
            comparison = a.name.localeCompare(b.name);
          } else if (sortBy === 'status') {
            comparison = (a.status || '').localeCompare(b.status || '');
          } else if (sortBy === 'created_at') {
            comparison = new Date(b.created_at) - new Date(a.created_at);
          } else if (sortBy === 'total_budget') {
            comparison = (parseFloat(b.total_budget) || 0) - (parseFloat(a.total_budget) || 0);
          }

          return sortOrder === 'asc' ? comparison : -comparison;
        });

        setMediaPlans(sortedPlans);
      }
    } catch (error) {
      console.error('Error updating media plan pin status:', error);
      setError('Failed to update media plan. Please try again.');
    }
    if (onClose) onClose();
  };

  // Toggle dropdown
  const toggleDropdown = (planId, event) => {
    event.stopPropagation();
    if (openDropdownId === planId) {
      setOpenDropdownId(null);
    } else {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX - 100
      });
      setOpenDropdownId(planId);
    }
  };

  // Format helpers
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // If settings are loading, show loading state
  if (settingsLoading) {
    return (
      <div className="page-container">
        <div className="page-loading">Loading...</div>
      </div>
    );
  }

  // If media planner is not enabled, show message
  if (!mediaPlannerEnabled) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <FontAwesomeIcon icon={faLightbulb} className="empty-state-icon" />
          <h3>Media Planner Not Enabled</h3>
          <p>Contact your administrator to enable the Media Planner feature for your account.</p>
        </div>
      </div>
    );
  }

  // Channel/Platform display name - normalize for display
  const getChannelName = (channel) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'google_ads': 'Google Ads Search',
      'google-ads-search': 'Google Ads Search',
      'tiktok': 'TikTok'
    };
    return names[channel?.toLowerCase()] || channel || 'N/A';
  };

  // Table columns - matching PacingPage pattern
  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: '1.5fr',
      sortable: true,
      resizable: true,
      render: (plan) => (
        <div className="campaign-name-content">
          {plan.is_pinned && (
            <FontAwesomeIcon
              icon={faThumbtack}
              className="pinned-icon"
              title="Pinned media plan"
            />
          )}
          {editingPlanId === plan.id ? (
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editingPlanName}
                onChange={(e) => setEditingPlanName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUpdatePlan();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                className="inline-edit-input"
                autoFocus
                disabled={isUpdating}
              />
              <div className="inline-edit-actions">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUpdatePlan();
                  }}
                  disabled={!editingPlanName.trim() || isUpdating}
                  className="inline-edit-btn save-btn"
                  title="Save"
                >
                  {isUpdating ? '⟳' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  disabled={isUpdating}
                  className="inline-edit-btn cancel-btn"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="campaign-name-container">
              <span className="campaign-name-text" title="Media plan name">
                {plan.name}
              </span>
              <div className="campaign-name-actions">
                <button
                  className="edit-name-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditPlan(plan);
                  }}
                  title="Edit media plan name"
                >
                  ✏️
                </button>
              </div>
            </div>
          )}
          {plan.iteration_number > 0 && (
            <span className="count-badge" style={{ marginLeft: '8px' }}>v{plan.iteration_number + 1}</span>
          )}
        </div>
      )
    },
    {
      key: 'total_budget',
      label: 'Budget',
      width: '120px',
      sortable: true,
      render: (plan) => formatCurrency(plan.total_budget)
    },
    {
      key: 'date_range',
      label: 'Date Range',
      width: '180px',
      sortable: false,
      render: (plan) => `${formatDate(plan.start_date)} - ${formatDate(plan.end_date)}`
    },
    {
      key: 'channels',
      label: 'Channels',
      width: '180px',
      sortable: false,
      className: 'platform-info',
      render: (plan) => {
        const channels = plan.channels || [];
        if (channels.length === 0) return 'N/A';
        // Show all channels with normalized names, separated by comma
        return channels.map(ch => getChannelName(ch)).join(', ');
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      sortable: true,
      render: (plan) => <StatusBadge status={plan.status || 'draft'} />
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '120px',
      sortable: true,
      render: (plan) => formatDate(plan.created_at)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown)
  ];

  // Dropdown Menu Component
  const DropdownMenu = ({ plan, onClose }) => {
    if (!plan) return null;

    const handlePinClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await handlePin(plan, onClose);
    };

    const handleDeleteClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await handleDelete(plan, onClose);
    };

    return (
      <div
        className="sidebar-dropdown-menu"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onMouseDown={handlePinClick}
          className="sidebar-dropdown-item"
        >
          <FontAwesomeIcon icon={faThumbtack} />
          {plan.is_pinned ? 'Unpin' : 'Pin'}
        </button>
        <button
          onMouseDown={handleDeleteClick}
          className="sidebar-dropdown-item delete"
        >
          <FontAwesomeIcon icon={faTrash} />
          Delete
        </button>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>
            Media Plans
            <span className="count-badge">{pagination.totalItems || mediaPlans.length}</span>
          </h1>
          <p>AI-powered media planning for multi-channel campaigns</p>
        </div>
        <div className="header-right">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search media plans..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate('/media-plans/new')}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Media Plan
          </button>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
        </div>
      )}

      <DataTable
        data={mediaPlans}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={(plan, e) => {
          if (e.target.closest('.dropdown-container') ||
              e.target.closest('.sidebar-action-button')) {
            return;
          }
          navigate(`/media-plans/${plan.id}`);
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faLightbulb} className="empty-state-icon" />
            <h3>No media plans yet</h3>
            <p>Create your first AI-powered media plan to get started</p>
            <button className="btn btn-primary" onClick={() => navigate('/media-plans/new')}>
              <FontAwesomeIcon icon={faPlus} />
              Create Media Plan
            </button>
          </div>
        }
        className="media-plans-table"
        storageKey="media-plans"
      />

      {/* Dropdown Menu */}
      {openDropdownId && (
        <DropdownMenu
          plan={mediaPlans.find(p => p.id === openDropdownId)}
          onClose={() => setOpenDropdownId(null)}
        />
      )}
    </div>
  );
};

export default MediaPlansPage;
